import { track } from '../analytics/amplitude';
import { ASSET_CDN_FALLBACK } from '../analytics/events';
import { isCdnUrl, toLocalUrl } from './cdnUrl';
import { errorMessage } from './errorMessage';
import { isOnline } from './isOnline';

export type AssetType = 'splat' | 'preview-glb' | 'skybox' | 'glb';

export type LoadWithCdnFallbackContext = {
  assetType: AssetType;
  sceneId?: string | null;
};

/**
 * Loads an asset, falling back from CDN to local origin on CDN failure.
 *
 * - If the URL is not a CDN URL (no VITE_CDN_BASE_URL or already local),
 *   the loader is invoked once and the result returned as-is — no overhead.
 * - On CDN failure, logs a console.warn, retries once with the local URL,
 *   and emits an `Asset CDN Fallback` Amplitude event with the outcome.
 * - If the local retry also fails, the ORIGINAL CDN error is rethrown
 *   (it's the more informative one — the reason we attempted fallback).
 */
export const loadWithCdnFallback = async <T>(
  url: string,
  loader: (resolvedUrl: string) => Promise<T>,
  context: LoadWithCdnFallbackContext,
): Promise<T> => {
  if (!isCdnUrl(url)) {
    return loader(url);
  }

  try {
    return await loader(url);
  } catch (cdnError) {
    const localUrl = toLocalUrl(url);
    console.error('[CDN] Falling back to local', { url, localUrl, error: cdnError });

    try {
      const result = await loader(localUrl);
      track(ASSET_CDN_FALLBACK, {
        asset_type: context.assetType,
        scene_id: context.sceneId ?? null,
        cdn_url: url,
        local_url: localUrl,
        cdn_error_message: errorMessage(cdnError),
        local_error_message: null,
        fallback_succeeded: true,
        online: isOnline(),
      });
      console.error('[CDN] Loaded from local fallback', {
        cdnUrl: url,
        localUrl,
        assetType: context.assetType,
        sceneId: context.sceneId ?? null,
        cdnError,
      });
      return result;
    } catch (localError) {
      track(ASSET_CDN_FALLBACK, {
        asset_type: context.assetType,
        scene_id: context.sceneId ?? null,
        cdn_url: url,
        local_url: localUrl,
        cdn_error_message: errorMessage(cdnError),
        local_error_message: errorMessage(localError),
        fallback_succeeded: false,
        online: isOnline(),
      });
      console.error('[CDN] Both CDN and local load failed', {
        cdnUrl: url,
        localUrl,
        assetType: context.assetType,
        sceneId: context.sceneId ?? null,
        cdnError,
        localError,
      });
      throw cdnError;
    }
  }
};
